import React, { useState, useEffect } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  Button,
} from "reactstrap"

const SupplierModal = ({
  isOpen,
  toggle,
  supplierData: initialData = {},
  modalType = "", // "add" | "edit" | "view"
  onSave,
}) => {
  const [formData, setFormData] = useState({})
  const [errors, setErrors] = useState({})

  // Sync form data when modal opens / initial data changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        supplierName: "",
        supplierCode: "",
        supplierEmail: "",
        ...initialData,
      })
      setErrors({})
    }
  }, [isOpen, initialData])

  const isViewMode = modalType === "view"
  const isEditMode = modalType === "edit"
  const isAddMode = modalType === "add" || !initialData?.id

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.supplierName?.trim()) {
      newErrors.supplierName = "Supplier name is required"
    }

    if (!formData.supplierEmail?.trim()) {
      newErrors.supplierEmail = "Email is required"
    } else if (!/\S+@\S+\.\S+/.test(formData.supplierEmail)) {
      newErrors.supplierEmail = "Please enter a valid email address"
    }
    if (!formData.supplierCode?.trim()) {
      newErrors.supplierCode = "Supplier code is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = () => {
    if (isViewMode) {
      toggle()
      return
    }

    if (validateForm()) {
      const dataToSend = {
        ...formData,
        status: !!formData.status,
      }

      onSave(dataToSend)
    }
  }

  const title = isViewMode
    ? "View Supplier"
    : isEditMode
    ? "Edit Supplier"
    : "Add New Supplier"

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>{title}</ModalHeader>

      <ModalBody>
        <div className="row">
          {/* Supplier Name */}
          <div className="col-md-12 mb-3">
            <Label>
              Supplier Name <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              name="supplierName"
              value={formData.supplierName || ""}
              onChange={handleChange}
              disabled={isViewMode}
              placeholder="e.g. Global Traders Ltd"
              invalid={!!errors.supplierName}
            />
            {errors.supplierName && (
              <small className="text-danger">{errors.supplierName}</small>
            )}
          </div>

          {/* Supplier Code */}
          <div className="col-md-6 mb-3">
            <Label>
              Supplier Code <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              name="supplierCode"
              value={formData.supplierCode || ""}
              onChange={handleChange}
              disabled={isViewMode}
              placeholder="e.g. SUP-001, GT001"
              invalid={!!errors.supplierCode}
            />
            {errors.supplierCode && (
              <small className="text-danger">{errors.supplierCode}</small>
            )}
          </div>

          {/* Email */}
          <div className="col-md-6 mb-3">
            <Label>
              Email <span className="text-danger">*</span>
            </Label>
            <Input
              type="email"
              name="supplierEmail"
              value={formData.supplierEmail || ""}
              onChange={handleChange}
              disabled={isViewMode}
              placeholder="example@company.com"
              invalid={!!errors.supplierEmail}
            />
            {errors.supplierEmail && (
              <small className="text-danger">{errors.supplierEmail}</small>
            )}
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        {isViewMode ? (
          <Button color="secondary" onClick={toggle}>
            Close
          </Button>
        ) : (
          <>
            <Button color="secondary" onClick={toggle}>
              Cancel
            </Button>
            <Button color="primary" onClick={handleSubmit}>
              {isAddMode ? "Create Supplier" : "Update Supplier"}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  )
}

export default SupplierModal
